/**
 * Der Reiter „Neu“ in Zuhause — „Neu in Vespera“. (13.10.2026, Bahn zuhause-neu)
 *
 * Martin hat den Entwurf freigegeben. Die Karten selbst (Inhalt, Sichtbarkeit, Filter, Ungesehenes) stehen in
 * shared/neuigkeiten-karten.ts; hier nur das Zeichnen, das Merken und die Wege.
 *
 * ## Knöpfe nur mit Wirkung
 *
 * Eine Karte bekommt einen Knopf nur, wenn `zielFuerKonto` ein Ziel liefert. Die Wege sind die, die die App
 * schon kennt (app.ts hört darauf):
 *   - Einstellungsseite   `vespera:go-settings` { sub }
 *   - Ansicht (Morse)     `vespera:go-view` { view }
 *   - Unterseite (Anker)  `vespera:go-seite` { key }
 *   - Liste „Was ist neu“ `vespera:was-ist-neu-oeffnen` — nur Konto 1: der Server gibt anderen eine leere Liste,
 *                         ein Knopf dorthin täte nichts.
 *
 * ## Gemerkt
 *
 * `localStorage` (mit try/catch; fällt er aus, gilt der Stand nur für diese Sitzung): die neueste `seit`-Version
 * der gezeigten Karten, gemerkt beim Öffnen des Reiters. Der Punkt am Reiter steht, solange eine Karte neuer ist.
 * Das „Neu“-Schild und die Zahl im Satz gelten für den Besuch, in dem der Reiter geöffnet wurde.
 */
import {
  filtere,
  kartenFuerKonto,
  istKonto1,
  NEU_FUER_TEXT,
  neuGesehenSchluessel,
  neuesteSeit,
  neuSatz,
  themenMitKarten,
  ungesehene,
  zielFuerKonto,
  type NeuFeatures,
  type NeuKarte,
  type NeuThema,
  type NeuZiel,
} from "../../shared/neuigkeiten-karten.js";
import { istNeuerAls, istWebVersion } from "../../shared/web-version.js";
import { zeichen } from "./zeichen.js";

/** Wie die Zeichen für die Morse-Hervorhebung stehen: Punkt, Strich, Punkt, Punkt, Strich, Strich, Punkt. */
const MORSE_BILD = ["·", "–", "·", "·", "–", "–", "·"];

export interface Merker {
  lies: () => string;
  schreibe: (version: string) => void;
}

/** Der Merker im Browser — nie werfen, wenn der Speicher streikt (Privatmodus). */
function neuMerker(uid: () => number | null | undefined): Merker {
  // Je Konto: im selben Browser können zwei Konten angemeldet gewesen sein; der Merker des einen gilt nicht für das andere.
  const sitzung: Record<string, string> = {};
  const schluessel = (): string => neuGesehenSchluessel(uid());
  return {
    lies: () => {
      try {
        const roh = localStorage.getItem(schluessel()) ?? "";
        return istWebVersion(roh) ? roh : (sitzung[schluessel()] ?? "");
      } catch {
        return sitzung[schluessel()] ?? "";
      }
    },
    schreibe: (version) => {
      sitzung[schluessel()] = version;
      try {
        localStorage.setItem(schluessel(), version);
      } catch {
        /* Speicher voll oder gesperrt: gilt dann nur für diese Sitzung. */
      }
    },
  };
}

/** Das Ziel anstoßen — über die Ereignisse, auf die app.ts hört. */
export function geheZu(ziel: NeuZiel, doc: Document = document): void {
  const ev =
    ziel.art === "einstellungen"
      ? new CustomEvent("vespera:go-settings", { detail: { sub: ziel.sub } })
      : ziel.art === "ansicht"
        ? new CustomEvent("vespera:go-view", { detail: { view: ziel.view } })
        : new CustomEvent("vespera:go-seite", { detail: { key: ziel.key } });
  doc.dispatchEvent(ev);
}

interface NeuReiterOptionen {
  /** Das Konto, für das gezeichnet wird (wird bei jedem Zeichnen frisch gefragt). */
  uid: () => number | null | undefined;
  /** Die Freischaltungen des Kontos (aus /api/auth/me); fehlen sie, gelten Karten mit Freischaltung als nicht da. */
  features?: () => NeuFeatures;
  merker?: Merker;
  /** Wird gerufen, wenn sich der Punkt am Reiter ändern soll. */
  onPunkt?: (an: boolean) => void;
  navigiere?: (ziel: NeuZiel) => void;
  oeffneListe?: () => void;
}

export interface NeuReiter {
  /** Der Inhalt des Bereichs; das Bauteil leert und füllt ihn selbst. */
  wurzel: HTMLElement;
  /** Gibt es Ungesehenes (für den Punkt)? Rechnet ohne zu zeichnen. */
  hatUngesehenes: () => boolean;
  /** Der Reiter wird geöffnet: Stand des Besuchs festhalten, zeichnen, als gesehen merken. */
  zeige: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

export function baueNeuReiter(o: NeuReiterOptionen): NeuReiter {
  const merker = o.merker ?? neuMerker(o.uid);
  const navigiere = o.navigiere ?? ((z: NeuZiel) => geheZu(z));
  const oeffneListe = o.oeffneListe ?? (() => document.dispatchEvent(new CustomEvent("vespera:was-ist-neu-oeffnen")));
  const wurzel = el("div", "neu-reiter");
  let filter: NeuThema | "alle" = "alle";
  /** Die Karten, die in DIESEM Besuch neu sind (beim Öffnen festgehalten). */
  let neuIds = new Set<string>();

  const sichtbar = (): NeuKarte[] => kartenFuerKonto(o.uid(), o.features?.());
  const hatUngesehenes = (): boolean => ungesehene(sichtbar(), merker.lies()).length > 0;

  function karte(k: NeuKarte, hervor: boolean): HTMLElement {
    const uid = o.uid();
    const ziel = zielFuerKonto(k, uid);
    const a = el("article", hervor ? "neu-karte neu-karte--hervor" : "neu-karte");
    a.dataset.id = k.id;
    a.dataset.thema = k.thema;
    a.dataset.fuer = k.fuer;
    const neuSchild = neuIds.has(k.id) ? el("span", "neu-schild neu-schild--neu", "Neu") : null;
    const fuerSchild = el("span", "neu-schild neu-schild--zielgruppe", NEU_FUER_TEXT[k.fuer]);
    const knopf = ziel ? el("button", "neu-knopf", ziel.knopf) : null;
    if (knopf && ziel) {
      knopf.type = "button";
      knopf.dataset.ziel = ziel.art;
      knopf.setAttribute("aria-label", `${ziel.knopf}: ${k.titel}`);
      knopf.addEventListener("click", () => navigiere(ziel));
    }
    const titel = el("h3", "neu-karte-titel", k.titel);
    const text = el("p", "neu-karte-text", k.text);
    if (hervor) {
      // Groß: beide Schilder oben, Titel, (bei Morse) das Punkt-Strich-Bild, Text, Knopf.
      const schilder = el("div", "neu-karte-schilder");
      if (neuSchild) schilder.append(neuSchild);
      schilder.append(fuerSchild);
      a.append(schilder, titel);
      if (k.thema === "morse") {
        const bild = el("div", "neu-morse-bild");
        bild.setAttribute("aria-hidden", "true");
        for (const z of MORSE_BILD) bild.append(el("span", z === "·" ? "neu-morse-punkt" : "neu-morse-strich"));
        a.append(bild);
      }
      a.append(text);
      if (knopf) a.append(knopf);
      return a;
    }
    // Klein: Zeichen, Titel und „Neu“ in einer Zeile; unten „für wen“ und der Knopf.
    const kopf = el("div", "neu-karte-kopf");
    const sym = el("span", "neu-karte-zeichen");
    sym.innerHTML = zeichen(k.zeichen, 22);
    kopf.append(sym, titel);
    if (neuSchild) kopf.append(neuSchild);
    const fuss = el("div", "neu-karte-fuss");
    fuss.append(fuerSchild);
    if (knopf) fuss.append(knopf);
    a.append(kopf, text, fuss);
    return a;
  }

  function zeichne(): void {
    const alle = sichtbar();
    wurzel.replaceChildren();

    const kopf = el("header", "neu-kopf");
    kopf.append(el("h2", "neu-titel", "Neu in Vespera"));
    const satz = el("p", "neu-satz", neuSatz(neuIds.size));
    satz.setAttribute("role", "status");
    kopf.append(satz);

    const leiste = el("div", "neu-filter");
    leiste.setAttribute("role", "group");
    leiste.setAttribute("aria-label", "Nach Thema filtern");
    const schilder: Array<{ id: NeuThema | "alle"; name: string }> = [{ id: "alle", name: "Alle" }, ...themenMitKarten(alle)];
    for (const s of schilder) {
      const b = el("button", "neu-filter-knopf", s.name);
      b.type = "button";
      b.dataset.filter = s.id;
      b.setAttribute("aria-pressed", String(s.id === filter));
      b.classList.toggle("is-active", s.id === filter);
      b.addEventListener("click", () => {
        filter = s.id;
        zeichne();
        wurzel.querySelector<HTMLElement>(`.neu-filter-knopf[data-filter="${s.id}"]`)?.focus();
      });
      leiste.append(b);
    }

    const gezeigt = filtere(alle, filter);
    const liste = el("div", "neu-liste");
    gezeigt.forEach((k, i) => liste.append(karte(k, i === 0)));

    wurzel.append(kopf, leiste, liste);

    // „Was ist neu“ öffnet die Liste der Sätze — die gibt es nur für Konto 1.
    if (istKonto1(o.uid())) {
      const fuss = el("div", "neu-fuss");
      fuss.append(el("p", "neu-fuss-text", "Mehr als das? Die ganze Liste mit allen Änderungen."));
      const b = el("button", "neu-knopf neu-knopf--leise", "Was ist neu");
      b.type = "button";
      b.dataset.ziel = "liste";
      b.addEventListener("click", oeffneListe);
      fuss.append(b);
      wurzel.append(fuss);
    }
  }

  function zeige(): void {
    const alle = sichtbar();
    const gesehen = merker.lies();
    neuIds = new Set(ungesehene(alle, gesehen).map((k) => k.id));
    filter = "alle";
    zeichne();
    const neueste = neuesteSeit(alle);
    if (neueste && (!istWebVersion(gesehen) || istNeuerAls(neueste, gesehen))) merker.schreibe(neueste);
    o.onPunkt?.(false);
  }

  return { wurzel, hatUngesehenes, zeige };
}
